"use client";

import { GitBranch } from "lucide-react";
import React from "react";

import { Card, CardContent } from "@/app/_module/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import { useAgentStatusBreakdown } from "../hooks/useAgentStatusBreakdown";

/**
 * Status Overview summary — one quiet card:
 *   1. Funnel row: system stages as typographic stats (count + colored dot)
 *   2. Custom pipelines: every custom stage anyone in the hierarchy built,
 *      with assigned-lead count and the stage owner(s)
 * All numbers are ASSIGNED leads (agents + admins; owners excluded) across the
 * agency hierarchy. Hovering any stat or pill shows WHO holds how many.
 */

const FUNNEL: { key: string; label: string; dot: string }[] = [
  { key: "new lead", label: "Leads", dot: "#3b82f6" },
  { key: "appt", label: "Appointments", dot: "#0891b2" },
  { key: "underwriting", label: "Underwriting", dot: "#f59e0b" },
  { key: "sold & approved", label: "Sold & Approved", dot: "#059669" },
  { key: "no sale", label: "No Sale", dot: "#dc2626" },
  { key: "bad number", label: "Bad Number", dot: "#ef4444" },
  { key: "do not call", label: "Do Not Call", dot: "#92400e" },
  { key: "declined", label: "Declined", dot: "#6b7280" },
  { key: "lapsed", label: "Lapsed", dot: "#111827" },
];

const ALIASES: Record<string, string> = {
  new: "new lead",
  leads: "new lead",
  appointment: "appt",
  sold: "sold & approved",
  approved: "sold & approved",
};

const canonicalize = (rawName: string) => {
  const lower = rawName.trim().toLowerCase();
  return ALIASES[lower] || lower;
};

/** Ranked "who holds what" list for a stage — shared by stats and pills */
function HoldersTooltip({ title, holders }: { title: string; holders: { name: string; count: number }[] }) {
  const total = holders.reduce((s, h) => s + h.count, 0);
  // Show everyone; past ~10 rows the list scrolls inside the tooltip
  const top = holders;
  const rest = 0;
  return (
    <div className="min-w-[190px]">
      <div className="flex items-baseline justify-between gap-4 pb-1.5 mb-1.5 border-b border-white/15">
        <span className="font-semibold">{title}</span>
        <span className="tabular-nums opacity-80">{total.toLocaleString()}</span>
      </div>
      {holders.length === 0 ? (
        <div className="text-white/60 py-0.5">No leads assigned in this stage</div>
      ) : (
        <div className="space-y-0.5 max-h-[260px] overflow-y-auto pr-1">
          {top.map((h) => (
            <div key={h.name} className="flex items-baseline justify-between gap-4">
              <span className="truncate max-w-[150px]">{h.name}</span>
              <span className="tabular-nums font-semibold">{h.count.toLocaleString()}</span>
            </div>
          ))}
          {rest > 0 && <div className="text-white/60 pt-0.5">+{rest} more</div>}
        </div>
      )}
    </div>
  );
}

export const AssignedStagesBoxes = function () {
  const { data, isLoading } = useAgentStatusBreakdown();

  // stage (canonical) -> total, and stage -> ranked holders [{name, count}]
  const systemCounts: Record<string, number> = {};
  const holdersByStage: Record<string, { name: string; count: number }[]> = {};
  const agentNames = data?.agentNames || {};
  for (const [agentId, stages] of Object.entries(data?.agents || {})) {
    const agentName = agentNames[agentId] || "Unknown agent";
    for (const [rawName, rawCount] of Object.entries(stages)) {
      const count = typeof rawCount === "number" ? rawCount : 0;
      if (count <= 0) continue;
      const canonical = canonicalize(rawName);
      systemCounts[canonical] = (systemCounts[canonical] || 0) + count;
      if (!holdersByStage[canonical]) holdersByStage[canonical] = [];
      holdersByStage[canonical].push({ name: agentName, count });
    }
  }
  for (const list of Object.values(holdersByStage)) list.sort((a, b) => b.count - a.count);
  // Only pipelines that actually HOLD assigned leads — empty ones are noise
  const customStages = (data?.customStages || []).filter((s) => s.assignedCount > 0);

  if (isLoading && !data) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-7 w-16 rounded bg-gray-100 animate-pulse" />
                <div className="h-3 w-20 rounded bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Card>
        <CardContent className="py-5">
          {/* Funnel: system stages as quiet stats; hover = who holds what */}
          <div className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-x-6 gap-y-4">
            {FUNNEL.map((stage) => (
              <Tooltip key={stage.key}>
                <TooltipTrigger asChild>
                  <div className="cursor-default rounded-lg -mx-2 px-2 py-1 hover:bg-gray-50 transition-colors">
                    <div className="text-2xl font-bold text-gray-900 tabular-nums">
                      {(systemCounts[stage.key] || 0).toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.dot }} />
                      <span className="text-xs text-gray-500 truncate">{stage.label}</span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <HoldersTooltip title={stage.label} holders={holdersByStage[stage.key] || []} />
                </TooltipContent>
              </Tooltip>
            ))}
          </div>

          {/* Custom pipelines: every stage anyone built, owner-attributed */}
          {customStages.length > 0 && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-1.5 mb-2.5">
                <GitBranch className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Custom Pipelines
                </span>
                <span className="text-xs text-gray-400">· assigned leads in each · hover for who</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {customStages.map((stage) => (
                  <Tooltip key={stage.name}>
                    <TooltipTrigger asChild>
                      <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/60 pl-3 pr-2 py-1.5 cursor-default hover:bg-gray-100/80 transition-colors">
                        <span className="text-sm font-medium text-gray-800">{stage.name}</span>
                        <span
                          className={`text-xs font-semibold tabular-nums rounded-md px-1.5 py-0.5 ${
                            stage.assignedCount > 0
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-200/70 text-gray-500"
                          }`}
                        >
                          {stage.assignedCount.toLocaleString()}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      <div>
                        <HoldersTooltip
                          title={stage.name}
                          holders={holdersByStage[canonicalize(stage.name)] || []}
                        />
                        {stage.owners.length > 0 && (
                          <div className="mt-1.5 pt-1.5 border-t border-white/15 text-white/60">
                            Pipeline by {stage.owners.join(", ")}
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
